import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft,
  FileText,
  Building,
  User,
  Calendar,
  DollarSign,
  Clock,
  QrCode,
  Hash,
  CheckCircle,
  XCircle,
  Pause,
  AlertTriangle,
  Receipt,
  Package,
  Percent,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useState } from 'react';
import { compraItems } from '@/components/TipoFacturaSelector';

const estadoVariants: Record<string, 'default' | 'success' | 'destructive' | 'warning' | 'info'> = {
  pendiente: 'warning',
  aprobada: 'success',
  rechazada: 'destructive',
  en_espera: 'info',
};

const estadoLabels: Record<string, string> = {
  pendiente: 'Pendiente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  en_espera: 'En Espera',
};

function isOverdue(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  return date < new Date();
}

// Configuración de tipos de factura
const tipoFacturaLabels: Record<string, { label: string; color: string }> = {
  gasto: { label: 'Gasto', color: 'bg-orange-100 text-orange-800' },
  compra: { label: 'Compra', color: 'bg-blue-100 text-blue-800' },
  nota_credito: { label: 'Nota de Crédito', color: 'bg-green-100 text-green-800' },
};

// Configuración de subtipos
const subtiposLabels: Record<string, Record<string, string>> = {
  gasto: {
    servicios_publicos: 'Servicios Públicos',
    alquiler: 'Alquiler / Arrendamiento',
    mantenimiento: 'Mantenimiento y Reparaciones',
    gastos_oficina: 'Gastos de Oficina',
    transporte: 'Transporte y Viáticos',
    seguros: 'Seguros',
    impuestos: 'Impuestos y Tasas',
  },
  compra: {
    materia_prima: 'Materia Prima',
    inventario: 'Inventario / Mercancía',
    equipos: 'Equipos y Maquinaria',
    vehiculos: 'Vehículos',
    mobiliario: 'Mobiliario y Enseres',
    herramientas: 'Herramientas',
  },
  nota_credito: {
    devolucion: 'Devolución de Mercancía',
    descuento_pronto_pago: 'Descuento por Pronto Pago',
    ajuste_precio: 'Ajuste de Precio',
    error_facturacion: 'Error en Facturación',
    bonificacion: 'Bonificación',
    anulacion_parcial: 'Anulación Parcial',
  },
};

// Configuración de tipos de IVA
const tiposIvaLabels: Record<string, string> = {
  '0': 'Varios',
  'TAR0': 'Ventas Excluidas',
  'TAR1': 'Tarifa 19%',
  'TAR2': 'Tarifa 5%',
  'TAR3': 'Tarifa 35%',
  'TAR4': 'Tarifa 45%',
  'TAR5': 'Tarifa 60%',
  'TAR6': 'Otras Tarifas',
  'TAR7': 'IVA 1%',
  'TAR8': 'IVA 19% Importaciones',
  'TAR9': 'IVA 19% Servicios',
};

// Extraer datos del QR Code del XML
function extractQRCodeData(xml: string | null | undefined): Record<string, string> | null {
  if (!xml) return null;
  const qrMatch = xml.match(/<(?:cbc:)?QRCode[^>]*>([^<]*)<\/(?:cbc:)?QRCode>/i);
  if (!qrMatch?.[1]) return null;

  const qrData: Record<string, string> = {};
  const pairs = qrMatch[1].split(/\s+/);
  for (const pair of pairs) {
    const [key, value] = pair.split('=');
    if (key && value) {
      qrData[key] = value;
    }
  }
  return Object.keys(qrData).length > 0 ? qrData : null;
}

// Helper functions for XML parsing
function getTextContent(xml: string, tagPattern: string): string | undefined {
  const patterns = tagPattern.split('|');
  for (const pattern of patterns) {
    const regexWithNs = new RegExp(`<(?:[a-z]+:)?${pattern}[^>]*>([^<]*)<\/(?:[a-z]+:)?${pattern}>`, 'i');
    const matchWithNs = xml.match(regexWithNs);
    if (matchWithNs?.[1]) return matchWithNs[1].trim();
  }
  return undefined;
}

function getAttributeValue(xml: string, tagPattern: string, attribute: string): string | undefined {
  const patterns = tagPattern.split('|');
  for (const pattern of patterns) {
    const regex = new RegExp(`<(?:[a-z]+:)?${pattern}[^>]*${attribute}="([^"]*)"`, 'i');
    const match = xml.match(regex);
    if (match?.[1]) return match[1].trim();
  }
  return undefined;
}

function extractSection(xml: string, sectionName: string): string | undefined {
  const patterns = [
    new RegExp(`<(?:[a-z]+:)?${sectionName}[^>]*>[\\s\\S]*?<\/(?:[a-z]+:)?${sectionName}>`, 'i'),
    new RegExp(`<${sectionName}[^>]*>[\\s\\S]*?<\/${sectionName}>`, 'i'),
  ];
  for (const regex of patterns) {
    const match = xml.match(regex);
    if (match?.[0]) return match[0];
  }
  return undefined;
}

function extractAllSections(xml: string, sectionName: string): string[] {
  const sections: string[] = [];
  const patterns = [
    new RegExp(`<(?:[a-z]+:)?${sectionName}[^>]*>[\\s\\S]*?<\/(?:[a-z]+:)?${sectionName}>`, 'gi'),
    new RegExp(`<${sectionName}[^>]*>[\\s\\S]*?<\/${sectionName}>`, 'gi'),
  ];
  for (const regex of patterns) {
    const matches = xml.match(regex);
    if (matches && matches.length > 0) sections.push(...matches);
  }
  return sections;
}

function parseNumber(value: string | undefined): number {
  if (!value) return 0;
  const num = parseFloat(value.replace(/[^\d.-]/g, ''));
  return isNaN(num) ? 0 : num;
}

// Interfaces for extracted data
interface TaxSubtotal {
  taxable_amount: number;
  tax_amount: number;
  percent: number;
  tax_scheme_id: string;
  tax_scheme_name: string;
}

interface TaxTotal {
  tax_amount: number;
  rounding_amount?: number;
  subtotals: TaxSubtotal[];
}

interface InvoiceLine {
  id: string;
  invoiced_quantity: number;
  unit_code: string;
  line_extension_amount: number;
  description: string;
  sellers_item_id?: string;
  standard_item_id?: string;
  price_amount: number;
  base_quantity?: number;
  allowance_charge?: number;
  tax_amount?: number;
}

// Extract InvoiceTypeCode
function extractInvoiceTypeCode(xml: string | null | undefined): string | null {
  if (!xml) return null;
  const cdataMatch = xml.match(/<!\[CDATA\[([\s\S]*?)\]\]>/);
  const invoiceXml = cdataMatch?.[1] || xml;
  return getTextContent(invoiceXml, 'InvoiceTypeCode') || null;
}

// Extract TaxTotals
function extractTaxTotals(xml: string | null | undefined): TaxTotal[] {
  if (!xml) return [];
  const cdataMatch = xml.match(/<!\[CDATA\[([\s\S]*?)\]\]>/);
  const invoiceXml = cdataMatch?.[1] || xml;

  const taxTotals: TaxTotal[] = [];
  const taxTotalSections = extractAllSections(invoiceXml, 'TaxTotal');

  for (const taxTotalXml of taxTotalSections) {
    const taxAmount = parseNumber(getTextContent(taxTotalXml, 'TaxAmount'));
    const roundingAmount = parseNumber(getTextContent(taxTotalXml, 'RoundingAmount'));

    const subtotals: TaxSubtotal[] = [];
    const taxSubtotalSections = extractAllSections(taxTotalXml, 'TaxSubtotal');

    for (const subtotalXml of taxSubtotalSections) {
      const taxableAmount = parseNumber(getTextContent(subtotalXml, 'TaxableAmount'));
      const subtotalTaxAmount = parseNumber(getTextContent(subtotalXml, 'TaxAmount'));
      const taxCategorySection = extractSection(subtotalXml, 'TaxCategory');
      const percent = parseNumber(getTextContent(taxCategorySection || '', 'Percent'));
      const taxSchemeSection = extractSection(subtotalXml, 'TaxScheme');
      const taxSchemeId = getTextContent(taxSchemeSection || '', 'ID') || '';
      const taxSchemeName = getTextContent(taxSchemeSection || '', 'Name') || '';

      subtotals.push({
        taxable_amount: taxableAmount,
        tax_amount: subtotalTaxAmount,
        percent,
        tax_scheme_id: taxSchemeId,
        tax_scheme_name: taxSchemeName,
      });
    }

    if (taxAmount > 0 || subtotals.length > 0) {
      taxTotals.push({
        tax_amount: taxAmount,
        rounding_amount: roundingAmount || undefined,
        subtotals,
      });
    }
  }

  return taxTotals;
}

// Extract InvoiceLines
function extractInvoiceLines(xml: string | null | undefined): InvoiceLine[] {
  if (!xml) return [];
  const cdataMatch = xml.match(/<!\[CDATA\[([\s\S]*?)\]\]>/);
  const invoiceXml = cdataMatch?.[1] || xml;

  const lines: InvoiceLine[] = [];
  // Soportar InvoiceLine (facturas) y CreditNoteLine (notas credito)
  let invoiceLineSections = extractAllSections(invoiceXml, 'InvoiceLine');
  if (invoiceLineSections.length === 0) {
    invoiceLineSections = extractAllSections(invoiceXml, 'CreditNoteLine');
  }

  for (const lineXml of invoiceLineSections) {
    const id = getTextContent(lineXml, 'ID') || '';
    const quantityMatch = lineXml.match(/<(?:[a-z]+:)?(?:Invoiced|Credited)Quantity[^>]*unitCode="([^"]*)"[^>]*>([^<]*)<\/(?:[a-z]+:)?(?:Invoiced|Credited)Quantity>/i);
    const invoicedQuantity = parseNumber(quantityMatch?.[2] || getTextContent(lineXml, 'InvoicedQuantity') || getTextContent(lineXml, 'CreditedQuantity'));
    const unitCode = quantityMatch?.[1] || getAttributeValue(lineXml, 'InvoicedQuantity', 'unitCode') || getAttributeValue(lineXml, 'CreditedQuantity', 'unitCode') || '';
    const lineExtensionAmount = parseNumber(getTextContent(lineXml, 'LineExtensionAmount'));

    const itemSection = extractSection(lineXml, 'Item');
    const description = getTextContent(itemSection || lineXml, 'Description') || getTextContent(itemSection || lineXml, 'Name') || '';

    const sellersItemSection = extractSection(itemSection || '', 'SellersItemIdentification');
    const sellersItemId = getTextContent(sellersItemSection || '', 'ID');

    const standardItemSection = extractSection(itemSection || '', 'StandardItemIdentification');
    const standardItemId = getTextContent(standardItemSection || '', 'ID');

    const priceSection = extractSection(lineXml, 'Price');
    const priceAmount = parseNumber(getTextContent(priceSection || '', 'PriceAmount'));
    const baseQuantity = parseNumber(getTextContent(priceSection || '', 'BaseQuantity'));

    const allowanceChargeSection = extractSection(lineXml, 'AllowanceCharge');
    const allowanceCharge = parseNumber(getTextContent(allowanceChargeSection || '', 'Amount'));

    const lineTaxSection = extractSection(lineXml, 'TaxTotal');
    const lineTaxAmount = parseNumber(getTextContent(lineTaxSection || '', 'TaxAmount'));

    lines.push({
      id,
      invoiced_quantity: invoicedQuantity,
      unit_code: unitCode,
      line_extension_amount: lineExtensionAmount,
      description,
      sellers_item_id: sellersItemId,
      standard_item_id: standardItemId,
      price_amount: priceAmount,
      base_quantity: baseQuantity || undefined,
      allowance_charge: allowanceCharge || undefined,
      tax_amount: lineTaxAmount || undefined,
    });
  }

  return lines;
}

// Invoice Type Code labels
const invoiceTypeLabels: Record<string, string> = {
  '01': 'Factura de Venta',
  '02': 'Factura de Exportacion',
  '03': 'Factura por Contingencia',
  '04': 'Factura por Contingencia DIAN',
  '91': 'Nota Credito',
  '92': 'Nota Debito',
};

export default function FacturaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { usuario: currentUser } = useAuth();
  const isAdmin = currentUser?.rol === 'admin';
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: string;
    estado: string;
  }>({ open: false, action: '', estado: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationAlert, setValidationAlert] = useState(false);
  const [showInvoiceLines, setShowInvoiceLines] = useState(false);
  const [showTaxDetails, setShowTaxDetails] = useState(false);

  const { data: factura, isLoading, refetch } = useQuery({
    queryKey: ['factura', id],
    queryFn: () => api.getFactura(id!),
    enabled: !!id,
  });

  const handleChangeEstado = async () => {
    if (!confirmDialog.estado || !id) return;
    setIsProcessing(true);
    try {
      await api.updateEstado(id, confirmDialog.estado);
      // Refetch inmediato para actualizar los datos incluyendo el historial
      await refetch();
      // Tambien invalidar las queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      setConfirmDialog({ open: false, action: '', estado: '' });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Factura no encontrada</h2>
          <Button onClick={() => navigate('/')} className="mt-4">
            Volver al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const qrData = extractQRCodeData(factura.xml_original);
  const invoiceTypeCode = extractInvoiceTypeCode(factura.xml_original);
  const taxTotals = extractTaxTotals(factura.xml_original);
  const invoiceLines = extractInvoiceLines(factura.xml_original);
  const vencida = isOverdue(factura.fecha_vencimiento) && factura.estado === 'pendiente';

  // Parse per-line IVA assignments
  const lineasIvaMap = (() => {
    if (!factura.lineas_iva) return new Map<string, string>();
    try {
      const parsed = JSON.parse(factura.lineas_iva);
      const map = new Map<string, string>();
      for (const la of parsed) {
        map.set(la.line_id, la.tipo_iva);
      }
      return map;
    } catch { return new Map<string, string>(); }
  })();

  // Parse per-line subtipo assignments
  const lineasSubtipoMap = (() => {
    if (!factura.lineas_subtipo) return new Map<string, string>();
    try {
      const parsed = JSON.parse(factura.lineas_subtipo);
      const map = new Map<string, string>();
      for (const ls of parsed) {
        map.set(ls.line_id, ls.subtipo_factura);
      }
      return map;
    } catch { return new Map<string, string>(); }
  })();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold">Factura {factura.numero}</h1>
                  <Badge variant={estadoVariants[factura.estado]}>
                    {estadoLabels[factura.estado]}
                  </Badge>
                  {factura.prioridad === 'critica' && (
                    <Badge variant="destructive">Critica</Badge>
                  )}
                  {vencida && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Vencida
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Cargada el {formatDateTime(factura.fecha_carga)}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {factura.estado === 'aprobada' && !isAdmin ? (
                <span className="text-xs text-muted-foreground">Solo un admin puede cambiar el estado</span>
              ) : (
                <>
                  {factura.estado !== 'aprobada' && (
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        if (!factura.tipo_factura || !factura.tipo_iva) {
                          setValidationAlert(true);
                          return;
                        }
                        setConfirmDialog({ open: true, action: 'aprobar', estado: 'aprobada' });
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Aprobar
                    </Button>
                  )}
                  {factura.estado !== 'en_espera' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmDialog({ open: true, action: 'poner en espera', estado: 'en_espera' })}
                    >
                      <Pause className="h-4 w-4 mr-1" />
                      En Espera
                    </Button>
                  )}
                  {factura.estado !== 'rechazada' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setConfirmDialog({ open: true, action: 'rechazar', estado: 'rechazada' })}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Rechazar
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* Informacion General */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Informacion General
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Numero de Factura</p>
                <p className="font-medium">{factura.numero}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estado</p>
                <Badge variant={estadoVariants[factura.estado]}>
                  {estadoLabels[factura.estado]}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prioridad</p>
                <Badge variant={factura.prioridad === 'critica' ? 'destructive' : 'default'}>
                  {factura.prioridad === 'critica' ? 'Critica' : 'Normal'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Moneda</p>
                <p className="font-medium">{factura.moneda || 'COP'}</p>
              </div>
              {invoiceTypeCode && (
                <div>
                  <p className="text-sm text-muted-foreground">Tipo Documento DIAN</p>
                  <Badge className="bg-indigo-100 text-indigo-800">
                    {invoiceTypeCode} - {invoiceTypeLabels[invoiceTypeCode] || 'Desconocido'}
                  </Badge>
                </div>
              )}
              {factura.tipo_factura && (
                <div>
                  <p className="text-sm text-muted-foreground">Tipo de Factura</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={tipoFacturaLabels[factura.tipo_factura]?.color}>
                      {tipoFacturaLabels[factura.tipo_factura]?.label || factura.tipo_factura}
                    </Badge>
                    {factura.subtipo_factura && (
                      <span className="text-sm font-medium">
                        - {subtiposLabels[factura.tipo_factura]?.[factura.subtipo_factura] || factura.subtipo_factura}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {factura.tipo_iva && (
                <div>
                  <p className="text-sm text-muted-foreground">Tipo de IVA</p>
                  <Badge className="bg-purple-100 text-purple-800">
                    {tiposIvaLabels[factura.tipo_iva] || factura.tipo_iva}
                  </Badge>
                </div>
              )}
              {factura.notas && (
                <div>
                  <p className="text-sm text-muted-foreground">Notas</p>
                  <p className="text-sm">{factura.notas}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fechas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Fechas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Fecha de Emision</p>
                <p className="font-medium">{formatDate(factura.fecha_emision)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fecha de Vencimiento</p>
                <p className={`font-medium ${vencida ? 'text-red-600' : ''}`}>
                  {factura.fecha_vencimiento ? formatDate(factura.fecha_vencimiento) : 'No especificada'}
                  {vencida && ' (Vencida)'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Fecha de Carga</p>
                <p className="font-medium">{formatDateTime(factura.fecha_carga)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ultima Actualizacion</p>
                <p className="font-medium">{formatDateTime(factura.updated_at)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Valores */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Valores
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Subtotal</p>
                <p className="font-medium">{formatCurrency(factura.valor_subtotal || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IVA</p>
                <p className="font-medium">{formatCurrency(factura.valor_iva || 0)}</p>
              </div>
              <div className="pt-3 border-t">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(factura.valor_total)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Proveedor (Emisor) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Proveedor (Emisor)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Razon Social</p>
                <p className="font-medium">{factura.proveedor_razon_social || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">NIT</p>
                <p className="font-medium">{factura.proveedor_nit || 'N/A'}</p>
              </div>
              {factura.proveedor_email && (
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{factura.proveedor_email}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Receptor */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Receptor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Nombre</p>
                <p className="font-medium">{factura.receptor_nombre || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">NIT/CC</p>
                <p className="font-medium">{factura.receptor_nit || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Asignacion */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Asignacion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Usuario Asignado</p>
                <p className="font-medium">{factura.usuario_asignado_nombre || 'Sin asignar'}</p>
              </div>
              {factura.usuario_asignado_email && (
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{factura.usuario_asignado_email}</p>
                </div>
              )}
              {factura.servicio_nombre && (
                <div>
                  <p className="text-sm text-muted-foreground">Servicio</p>
                  <p className="font-medium">{factura.servicio_nombre}</p>
                  {factura.servicio_codigo && (
                    <p className="text-xs text-muted-foreground">Codigo: {factura.servicio_codigo}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* CUFE/CUDE */}
          <Card className="md:col-span-2 lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5" />
                Identificadores DIAN
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <p className="text-sm text-muted-foreground">CUFE (Codigo Unico de Factura Electronica)</p>
                <p className="font-mono text-xs break-all bg-gray-100 p-2 rounded mt-1">
                  {factura.cufe || 'No disponible'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* QR Code Data */}
          {qrData && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  Datos del Codigo QR
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(qrData).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground">{key}</p>
                      <p className="font-medium text-sm">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lineas de Factura (Invoice Lines) - Colapsable */}
          {invoiceLines.length > 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setShowInvoiceLines(!showInvoiceLines)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    Lineas de Factura ({invoiceLines.length} items)
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-normal text-muted-foreground">
                      Total: {formatCurrency(invoiceLines.reduce((sum, line) => sum + line.line_extension_amount, 0))}
                    </span>
                    {showInvoiceLines ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              {showInvoiceLines && (
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left p-2">ID</th>
                          <th className="text-left p-2">Descripcion</th>
                          <th className="text-center p-2">Cantidad</th>
                          <th className="text-center p-2">Unidad</th>
                          <th className="text-right p-2">Precio Unit.</th>
                          <th className="text-right p-2">Subtotal</th>
                          <th className="text-right p-2">IVA</th>
                          <th className="text-right p-2">Tipo IVA</th>
                          <th className="text-right p-2">Subtipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceLines.map((line, index) => (
                          <tr key={index} className="border-t hover:bg-gray-50">
                            <td className="p-2">
                              <Badge variant="outline" className="font-mono">{line.id}</Badge>
                            </td>
                            <td className="p-2 max-w-xs">
                              <p className="truncate" title={line.description}>{line.description || 'Sin descripcion'}</p>
                              {(line.sellers_item_id || line.standard_item_id) && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {line.sellers_item_id && <span>SKU: {line.sellers_item_id}</span>}
                                  {line.sellers_item_id && line.standard_item_id && ' | '}
                                  {line.standard_item_id && <span>Cod: {line.standard_item_id}</span>}
                                </p>
                              )}
                            </td>
                            <td className="p-2 text-center">{line.invoiced_quantity}</td>
                            <td className="p-2 text-center">
                              <Badge variant="secondary" className="text-xs">{line.unit_code || 'UN'}</Badge>
                            </td>
                            <td className="p-2 text-right">{formatCurrency(
                              (line.price_amount <= 1 && line.invoiced_quantity > 0)
                                ? line.line_extension_amount / line.invoiced_quantity
                                : line.price_amount
                            )}</td>
                            <td className="p-2 text-right font-medium">{formatCurrency(line.line_extension_amount)}</td>
                            <td className="p-2 text-right">
                              {line.tax_amount ? formatCurrency(line.tax_amount) : '-'}
                            </td>
                            <td className="p-2 text-right">
                              {lineasIvaMap.get(line.id) ? (
                                <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                                  {tiposIvaLabels[lineasIvaMap.get(line.id)!] || lineasIvaMap.get(line.id)}
                                </Badge>
                              ) : factura.tipo_iva ? (
                                <Badge variant="outline" className="text-purple-600 border-purple-200">
                                  {tiposIvaLabels[factura.tipo_iva] || factura.tipo_iva}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-2 text-right">
                              {lineasSubtipoMap.get(line.id) ? (
                                <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-[10px]">
                                  {compraItems.find(i => i.codigo === lineasSubtipoMap.get(line.id))?.nombre || lineasSubtipoMap.get(line.id)}
                                </Badge>
                              ) : factura.subtipo_factura ? (
                                <Badge variant="outline" className="text-orange-600 border-orange-200 text-[10px]">
                                  {compraItems.find(i => i.codigo === factura.subtipo_factura)?.nombre || factura.subtipo_factura}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 bg-gray-100 font-medium">
                          <td colSpan={5} className="p-2 text-right">Total Lineas:</td>
                          <td className="p-2 text-right">
                            {formatCurrency(invoiceLines.reduce((sum, line) => sum + line.line_extension_amount, 0))}
                          </td>
                          <td className="p-2 text-right">
                            {formatCurrency(invoiceLines.reduce((sum, line) => sum + (line.tax_amount || 0), 0))}
                          </td>
                          <td className="p-2"></td>
                          <td className="p-2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {invoiceLines.some(line => line.allowance_charge) && (
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                        <Receipt className="h-4 w-4" />
                        Cargos/Descuentos por linea:
                      </p>
                      <div className="mt-2 space-y-1">
                        {invoiceLines.filter(line => line.allowance_charge).map((line, index) => (
                          <p key={index} className="text-sm text-yellow-700">
                            Linea {line.id}: {formatCurrency(line.allowance_charge!)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Impuestos (Tax Totals) - Colapsable */}
          {taxTotals.length > 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setShowTaxDetails(!showTaxDetails)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Percent className="h-5 w-5" />
                    Impuestos (Detalle XML)
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-normal text-muted-foreground">
                      Total: {formatCurrency(taxTotals.reduce((sum, t) => sum + t.tax_amount, 0))}
                    </span>
                    {showTaxDetails ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              {showTaxDetails && (
                <CardContent>
                  <div className="space-y-4">
                    {taxTotals.map((taxTotal, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-sm text-muted-foreground">TaxTotal/TaxAmount</p>
                            <p className="font-medium">{formatCurrency(taxTotal.tax_amount)}</p>
                          </div>
                          {taxTotal.rounding_amount !== undefined && taxTotal.rounding_amount > 0 && (
                            <div>
                              <p className="text-sm text-muted-foreground">TaxTotal/RoundingAmount</p>
                              <p className="font-medium">{formatCurrency(taxTotal.rounding_amount)}</p>
                            </div>
                          )}
                        </div>
                        {taxTotal.subtotals.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm font-medium mb-2">Desglose por Tipo de Impuesto:</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-50">
                                    <th className="text-left p-2">TaxScheme/ID</th>
                                    <th className="text-left p-2">TaxScheme/Name</th>
                                    <th className="text-right p-2">TaxCategory/Percent</th>
                                    <th className="text-right p-2">TaxSubtotal/TaxableAmount</th>
                                    <th className="text-right p-2">TaxSubtotal/TaxAmount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {taxTotal.subtotals.map((subtotal, subIndex) => (
                                    <tr key={subIndex} className="border-t">
                                      <td className="p-2">
                                        <Badge variant="outline">{subtotal.tax_scheme_id || 'N/A'}</Badge>
                                      </td>
                                      <td className="p-2">{subtotal.tax_scheme_name || 'N/A'}</td>
                                      <td className="p-2 text-right">{subtotal.percent}%</td>
                                      <td className="p-2 text-right">{formatCurrency(subtotal.taxable_amount)}</td>
                                      <td className="p-2 text-right font-medium">{formatCurrency(subtotal.tax_amount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Historial */}
          {factura.historial && factura.historial.length > 0 && (
            <Card className="md:col-span-2 lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Historial de Estados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {factura.historial.map((item: any) => (
                    <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={estadoVariants[item.estado_nuevo]} className="text-xs">
                            {estadoLabels[item.estado_nuevo]}
                          </Badge>
                          {item.estado_anterior && (
                            <span className="text-xs text-muted-foreground">
                              (antes: {estadoLabels[item.estado_anterior]})
                            </span>
                          )}
                        </div>
                        {item.comentario && (
                          <p className="text-sm mt-1">{item.comentario}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {item.usuario_nombre && `Por ${item.usuario_nombre} - `}
                          {formatDateTime(item.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', estado: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar accion</DialogTitle>
            <DialogDescription>
              Esta seguro que desea {confirmDialog.action} esta factura?
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="font-medium">Factura: {factura.numero}</p>
                <p className="text-sm">Valor: {formatCurrency(factura.valor_total)}</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, action: '', estado: '' })} disabled={isProcessing}>
              Cancelar
            </Button>
            <Button onClick={handleChangeEstado} disabled={isProcessing}>
              {isProcessing ? 'Procesando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Alert Dialog */}
      <Dialog open={validationAlert} onOpenChange={setValidationAlert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Campos requeridos para aprobar
            </DialogTitle>
            <DialogDescription>
              Para aprobar esta factura es necesario asignar los siguientes campos:
              <div className="mt-4 space-y-2">
                <div className="p-3 bg-muted rounded-md">
                  <p className="font-medium">Factura: {factura.numero}</p>
                </div>
                <div className="space-y-1">
                  <p className={`text-sm flex items-center gap-2 ${factura.tipo_factura ? 'text-green-600' : 'text-red-600'}`}>
                    {factura.tipo_factura ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    Tipo de Factura: {factura.tipo_factura ? 'Asignado' : 'Sin asignar'}
                  </p>
                  <p className={`text-sm flex items-center gap-2 ${factura.tipo_iva ? 'text-green-600' : 'text-red-600'}`}>
                    {factura.tipo_iva ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    Tipo de IVA: {factura.tipo_iva ? 'Asignado' : 'Sin asignar'}
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setValidationAlert(false)}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
